import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, TextInput, Alert, KeyboardAvoidingView, Platform, Image } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import PageHeader from '@/components/PageHeader';
import PrimaryButton from '@/components/PrimaryButton';
import { useLanguage } from '@/contexts/LanguageContext';
import { apiPost } from '@/services/apiService';

export default function CSATScreen() {
  const router = useRouter();
  const { t } = useLanguage();
  // `source` drives the topic list and headline so the rating context
  // matches what the user just did. Currently supported values:
  //   - 'postcall'    → opened from app/video-call/[channel].tsx
  //   - 'trail_step'  → opened from a trail step's "Rate this step" card
  //   - undefined     → generic surface (Profile menu, etc.)
  // `rating` is forwarded from surfaces that present the star picker
  // before this screen (today: post-call only) so we hydrate
  // `selectedRating` on mount and skip a redundant tap.
  const { source, rating: ratingParam, returnTo } = useLocalSearchParams<{
    source?: string;
    rating?: string;
    returnTo?: string;
  }>();
  const isPostCall = source === 'postcall';
  const isTrailStep = source === 'trail_step';
  const initialRating = (() => {
    const parsed = Number(ratingParam);
    return Number.isInteger(parsed) && parsed >= 1 && parsed <= 5 ? parsed : 0;
  })();

  const [selectedRating, setSelectedRating] = useState<number>(initialRating);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    // PRD §5.13 — `csat_displayed`. Fires once on mount. `meeting_type`
    // is only meaningful for the post-call surface (Agora video room) —
    // we omit it for non-call surfaces (trail step, profile) so the
    // analytics field doesn't lie. The submission's synthetic
    // `_source:<surface>` topic tag carries the surface filter for the
    // dashboard.
    import('@/utils/clarityEvents')
      .then(({ logCsatDisplayed }) =>
        logCsatDisplayed(isPostCall ? { meeting_type: 'video' } : {}),
      )
      .catch(() => {});
  }, [isPostCall]);

  // Safe back: deep-link / replace() entries leave no history. Fall back to
  // home so we never throw "GO_BACK action not handled by any navigator".
  const safeBack = () => {
    // PRD §5.13 — `csat_dismissed`. Fired only when the user backs out
    // WITHOUT submitting (the submit handler has its own thank-you flow).
    // Captures abandonment regardless of how far the user got.
    if (!isSubmitted) {
      import('@/utils/clarityEvents')
        .then(({ logCsatDismissed }) => logCsatDismissed())
        .catch(() => {});
    }
    // If the caller supplied an explicit return destination (e.g. the trail
    // step screen passes returnTo=/trail/<slug> so we land on the trail
    // details page after rating), use it directly — this avoids relying on
    // the navigation stack when CSAT is reached via router.replace() across
    // layout boundaries.
    if (returnTo) {
      router.replace(returnTo as any);
    } else if (router.canGoBack()) {
      router.back();
    } else {
      router.replace('/diary');
    }
  };
  const scrollViewRef = useRef<ScrollView>(null);

  const MAX_CHARS = 300;

  const negativeTopics = [
    { key: 'crashes', label: t('csat.topics.negative.crashes') },
    { key: 'slowPerformance', label: t('csat.topics.negative.slowPerformance') },
    { key: 'confusingNavigation', label: t('csat.topics.negative.confusingNavigation') },
    { key: 'billResponses', label: t('csat.topics.negative.billResponses') },
    { key: 'notifications', label: t('csat.topics.negative.notifications') },
    { key: 'design', label: t('csat.topics.negative.design') },
    { key: 'meetings', label: t('csat.topics.negative.meetings') },
    { key: 'trail', label: t('csat.topics.negative.trail') },
    { key: 'diary', label: t('csat.topics.negative.diary') },
    { key: 'other', label: t('csat.topics.other') },
  ];

  const neutralTopics = [
    { key: 'navigation', label: t('csat.topics.neutral.navigation') },
    { key: 'billQuality', label: t('csat.topics.neutral.billQuality') },
    { key: 'design', label: t('csat.topics.neutral.design') },
    { key: 'performance', label: t('csat.topics.neutral.performance') },
    { key: 'notifications', label: t('csat.topics.neutral.notifications') },
    { key: 'meetings', label: t('csat.topics.neutral.meetings') },
    { key: 'trail', label: t('csat.topics.neutral.trail') },
    { key: 'diary', label: t('csat.topics.neutral.diary') },
    { key: 'community', label: t('csat.topics.neutral.community') },
    { key: 'other', label: t('csat.topics.other') },
  ];

  const positiveTopics = [
    { key: 'easeOfUse', label: t('csat.topics.positive.easeOfUse') },
    { key: 'billHelp', label: t('csat.topics.positive.billHelp') },
    { key: 'design', label: t('csat.topics.positive.design') },
    { key: 'meetings', label: t('csat.topics.positive.meetings') },
    { key: 'trail', label: t('csat.topics.positive.trail') },
    { key: 'diary', label: t('csat.topics.positive.diary') },
    { key: 'sobrietyClock', label: t('csat.topics.positive.sobrietyClock') },
    { key: 'community', label: t('csat.topics.positive.community') },
    { key: 'notifications', label: t('csat.topics.positive.notifications') },
    { key: 'other', label: t('csat.topics.other') },
  ];

  // Call-specific topic sets — used when source=postcall. Scoped to the
  // call experience itself, not the wider app.
  const callNegativeTopics = [
    { key: 'audioQuality', label: t('csat.callTopics.negative.audioQuality') },
    { key: 'videoQuality', label: t('csat.callTopics.negative.videoQuality') },
    { key: 'connectionDrops', label: t('csat.callTopics.negative.connectionDrops') },
    { key: 'sponsorPreparedness', label: t('csat.callTopics.negative.sponsorPreparedness') },
    { key: 'scheduling', label: t('csat.callTopics.negative.scheduling') },
    { key: 'other', label: t('csat.topics.other') },
  ];

  const callNeutralTopics = [
    { key: 'audioQuality', label: t('csat.callTopics.neutral.audioQuality') },
    { key: 'videoQuality', label: t('csat.callTopics.neutral.videoQuality') },
    { key: 'sponsorPreparedness', label: t('csat.callTopics.neutral.sponsorPreparedness') },
    { key: 'callDuration', label: t('csat.callTopics.neutral.callDuration') },
    { key: 'scheduling', label: t('csat.callTopics.neutral.scheduling') },
    { key: 'other', label: t('csat.topics.other') },
  ];

  const callPositiveTopics = [
    { key: 'clearAudio', label: t('csat.callTopics.positive.clearAudio') },
    { key: 'clearVideo', label: t('csat.callTopics.positive.clearVideo') },
    { key: 'sponsorHelp', label: t('csat.callTopics.positive.sponsorHelp') },
    { key: 'easyToConnect', label: t('csat.callTopics.positive.easyToConnect') },
    { key: 'callDuration', label: t('csat.callTopics.positive.callDuration') },
    { key: 'other', label: t('csat.topics.other') },
  ];

  // Trail-step specific topic sets — used when source=trail_step. Scoped
  // narrowly to the curriculum CONTENT itself: clarity, relevance,
  // length, examples, language, plus the audio narration as the audio
  // delivery of that same content. App-shell topics (navigation, perf,
  // design) intentionally excluded — those belong to the generic
  // Profile-page CSAT, and mixing them here muddies the per-step signal
  // we actually want from this surface.
  const trailNegativeTopics = [
    { key: 'contentDifficult', label: t('csat.trailTopics.negative.contentDifficult') },
    { key: 'contentNotRelevant', label: t('csat.trailTopics.negative.contentNotRelevant') },
    { key: 'contentTooShort', label: t('csat.trailTopics.negative.contentTooShort') },
    { key: 'contentTooLong', label: t('csat.trailTopics.negative.contentTooLong') },
    { key: 'contentFewExamples', label: t('csat.trailTopics.negative.contentFewExamples') },
    { key: 'audioQuality', label: t('csat.trailTopics.negative.audioQuality') },
    { key: 'other', label: t('csat.topics.other') },
  ];

  const trailNeutralTopics = [
    { key: 'contentDepth', label: t('csat.trailTopics.neutral.contentDepth') },
    { key: 'contentPace', label: t('csat.trailTopics.neutral.contentPace') },
    { key: 'contentExamples', label: t('csat.trailTopics.neutral.contentExamples') },
    { key: 'contentLanguage', label: t('csat.trailTopics.neutral.contentLanguage') },
    { key: 'audioExperience', label: t('csat.trailTopics.neutral.audioExperience') },
    { key: 'other', label: t('csat.topics.other') },
  ];

  const trailPositiveTopics = [
    { key: 'contentClarity', label: t('csat.trailTopics.positive.contentClarity') },
    { key: 'contentHelpful', label: t('csat.trailTopics.positive.contentHelpful') },
    { key: 'contentRelevant', label: t('csat.trailTopics.positive.contentRelevant') },
    { key: 'contentExamples', label: t('csat.trailTopics.positive.contentExamples') },
    { key: 'audioQuality', label: t('csat.trailTopics.positive.audioQuality') },
    { key: 'other', label: t('csat.topics.other') },
  ];

  const getTopicsForRating = () => {
    if (isPostCall) {
      if (selectedRating <= 2) return callNegativeTopics;
      if (selectedRating === 3) return callNeutralTopics;
      return callPositiveTopics;
    }
    if (isTrailStep) {
      if (selectedRating <= 2) return trailNegativeTopics;
      if (selectedRating === 3) return trailNeutralTopics;
      return trailPositiveTopics;
    }
    if (selectedRating <= 2) return negativeTopics;
    if (selectedRating === 3) return neutralTopics;
    return positiveTopics;
  };

  const getTopicsTitle = () => {
    if (selectedRating <= 2) return t('csat.topicsTitles.negative');
    if (selectedRating === 3) return t('csat.topicsTitles.neutral');
    return t('csat.topicsTitles.positive');
  };

  const toggleTopic = (topicKey: string) => {
    setSelectedTopics(prev => 
      prev.includes(topicKey) 
        ? prev.filter(t => t !== topicKey)
        : [...prev, topicKey]
    );
  };

  const handleRatingSelect = (rating: number) => {
    if (rating !== selectedRating) {
      setSelectedTopics([]);
    }
    setSelectedRating(rating);
  };

  const handleSubmit = async () => {
    if (selectedRating === 0) {
      Alert.alert(t('csat.alerts.selectRating'));
      return;
    }

    setIsSubmitting(true);

    try {
      // We tag the source as a synthetic topic key (`_source:<surface>`) so
      // analysts can split CSAT by surface without a schema change. The
      // tag uses an underscore prefix so it never collides with a real chip.
      // The generic surface (no `source` param) submits without a tag.
      const sourceTag = isPostCall
        ? '_source:postcall'
        : isTrailStep
          ? '_source:trail_step'
          : null;
      const tagged = sourceTag ? [...selectedTopics, sourceTag] : selectedTopics;

      const response = await apiPost('/api/csat', {
        rating: selectedRating,
        topics: tagged.length > 0 ? tagged : null,
        feedback: feedback.trim() || null
      });

      if (!response.success) {
        console.error('Error submitting CSAT:', response.error);
        Alert.alert(t('csat.alerts.error'));
      } else {
        setIsSubmitted(true);

        // PRD §4 — `csat_submitted` macro event. Fires only on the
        // happy path; cancellations and errors are not counted. Per PRD §7
        // dictionary, only `score` and (optional) `meeting_type` are allowed
        // — this generic CSAT screen has no meeting context.
        import('@/utils/clarityEvents')
          .then(({ logCsatSubmitted }) =>
            logCsatSubmitted({ score: selectedRating })
          )
          .catch(() => {});
      }
    } catch (error) {
      console.error('Error submitting CSAT:', error);
      Alert.alert(t('csat.alerts.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStar = (starNumber: number) => {
    const isFilled = starNumber <= selectedRating;
    return (
      <TouchableOpacity
        key={starNumber}
        style={styles.starButton}
        onPress={() => handleRatingSelect(starNumber)}
        activeOpacity={0.7}
      >
        <Svg width="32" height="32" viewBox="0 0 20 20" fill="none">
          <Path 
            fillRule="evenodd" 
            clipRule="evenodd" 
            d="M10.9202 3.48068L12.4431 6.52297C12.5923 6.82163 12.8804 7.02873 13.2145 7.07666L16.6211 7.56702C17.4628 7.68854 17.7977 8.70863 17.1886 9.29312L14.7252 11.6602C14.4831 11.893 14.3729 12.2276 14.4301 12.5562L15.0115 15.898C15.1547 16.7247 14.2748 17.3554 13.5225 16.9643L10.4777 15.3854C10.1792 15.2305 9.82168 15.2305 9.52232 15.3854L6.4775 16.9643C5.72519 17.3554 4.84533 16.7247 4.98937 15.898L5.56987 12.5562C5.62714 12.2276 5.51694 11.893 5.27485 11.6602L2.8114 9.29312C2.20226 8.70863 2.5372 7.68854 3.37889 7.56702L6.78554 7.07666C7.11961 7.02873 7.40856 6.82163 7.55781 6.52297L9.07979 3.48068C9.45638 2.72845 10.5436 2.72845 10.9202 3.48068Z" 
            fill={isFilled ? "#669AB7" : "transparent"}
            stroke={isFilled ? "#669AB7" : "#A19F97"}
            strokeWidth="1.5"
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </Svg>
      </TouchableOpacity>
    );
  };

  if (isSubmitted) {
    return (
      <SafeAreaView style={styles.container}>
        <PageHeader 
          title={t('csat.pageTitle')} 
          onBack={safeBack}
          backgroundColor="#F9F8F3"
        />
        <View style={styles.successContainer}>
          <View style={styles.successContent}>
            <Image 
              source={require('@/assets/images/flowers.png')} 
              style={styles.successImage}
              resizeMode="contain"
            />
            <Text style={styles.successTitle}>{t('csat.success.title')}</Text>
            <Text style={styles.successSubtitle}>{t('csat.success.subtitle')}</Text>
          </View>
          <View style={styles.successFooter}>
            <PrimaryButton 
              title={t('csat.success.button')}
              onPress={safeBack}
            />
          </View>
        </View>
      </SafeAreaView>
    );
  }

  const handleTextInputFocus = () => {
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);
  };

  return (
    <SafeAreaView style={styles.container}>
      <PageHeader 
        title={t('csat.pageTitle')} 
        onBack={safeBack}
        backgroundColor="#F9F8F3"
      />

      <KeyboardAvoidingView 
        style={styles.keyboardAvoidingView}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 0}
      >
        <ScrollView 
          ref={scrollViewRef}
          style={styles.scrollView} 
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={styles.scrollViewContent}
        >
          {/* Section 1: Stars Rating */}
          <View style={styles.starsSection}>
            <Text style={styles.title}>
              {isPostCall
                ? t('csat.callTitle')
                : isTrailStep
                  ? t('csat.trailTitle')
                  : t('csat.title')}
            </Text>
            <Text style={styles.subtitle}>{t('csat.starsInstruction')}</Text>
            <View style={styles.starsContainer}>
              {[1, 2, 3, 4, 5].map(renderStar)}
            </View>
          </View>

          <View style={styles.divider} />

          {/* Section 2: Topics (only show after rating selected) */}
          {selectedRating > 0 && (
            <>
              <View style={styles.topicsSection}>
                <Text style={styles.sectionTitle}>{getTopicsTitle()}</Text>
                <View style={styles.topicsContainer}>
                  {getTopicsForRating().map((topic) => {
                    const isSelected = selectedTopics.includes(topic.key);
                    return (
                      <TouchableOpacity
                        key={topic.key}
                        style={[
                          styles.topicChip,
                          isSelected && styles.topicChipSelected
                        ]}
                        onPress={() => toggleTopic(topic.key)}
                        activeOpacity={0.7}
                      >
                        {isSelected ? (
                          <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <Path d="M13.3337 4L6.00033 11.3333L2.66699 8" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </Svg>
                        ) : (
                          <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <Path d="M8 3.33301V12.6663" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <Path d="M3.33301 8H12.6663" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </Svg>
                        )}
                        <Text style={[
                          styles.topicChipText,
                          isSelected && styles.topicChipTextSelected
                        ]}>{topic.label}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>

              <View style={styles.divider} />

              {/* Section 3: Comment */}
              <View style={styles.commentSection}>
                <Text style={styles.sectionTitle}>{t('csat.commentTitle')}</Text>
                <View style={styles.textInputContainer}>
                  <TextInput
                    style={styles.textInput}
                    placeholder={t('csat.commentPlaceholder')}
                    placeholderTextColor="#A19F97"
                    value={feedback}
                    onChangeText={(text) => setFeedback(text.slice(0, MAX_CHARS))}
                    multiline
                    textAlignVertical="top"
                    onFocus={handleTextInputFocus}
                  />
                </View>
                <Text style={styles.charCount}>{MAX_CHARS - feedback.length}</Text>
              </View>
            </>
          )}

          {/* Footer inside ScrollView for keyboard handling */}
          <View style={styles.footer}>
            <PrimaryButton 
              title={isSubmitting ? t('csat.submitting') : t('csat.submit')}
              onPress={handleSubmit}
              disabled={isSubmitting || selectedRating === 0}
            />
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F8F3',
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    flexGrow: 1,
  },
  starsSection: {
    padding: 24,
    alignItems: 'center',
    gap: 16,
  },
  title: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    fontWeight: '400',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 28.8,
  },
  subtitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: '700',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 14,
  },
  starsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
  starButton: {
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  divider: {
    height: 1,
    backgroundColor: '#E7EAED',
    marginHorizontal: 24,
  },
  topicsSection: {
    padding: 24,
    gap: 24,
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: '700',
    color: '#151515',
    lineHeight: 14,
  },
  topicsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'center',
  },
  topicChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 8,
    paddingHorizontal: 8,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#F0EEE4',
    borderRadius: 17,
  },
  topicChipSelected: {
    backgroundColor: '#E6EBF0',
    borderColor: '#015480',
  },
  topicChipText: {
    fontSize: 12,
    fontFamily: 'Inter',
    fontWeight: '500',
    color: '#151515',
    lineHeight: 16.8,
  },
  topicChipTextSelected: {
    color: '#015480',
  },
  commentSection: {
    padding: 24,
    gap: 16,
  },
  textInputContainer: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#DAD8CF',
    borderRadius: 12,
    padding: 16,
    minHeight: 120,
  },
  textInput: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: '500',
    color: '#151515',
    lineHeight: 14,
    minHeight: 88,
  },
  charCount: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: '400',
    color: '#7E7D75',
    textAlign: 'right',
    lineHeight: 19.6,
  },
  footer: {
    padding: 24,
    paddingBottom: 32,
    backgroundColor: '#F9F8F3',
    marginTop: 'auto',
  },
  successContainer: {
    flex: 1,
    paddingHorizontal: 40,
  },
  successContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
  },
  successImage: {
    width: 260,
    height: 260,
  },
  successTitle: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    fontWeight: '400',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 28.8,
  },
  successSubtitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: '400',
    color: '#757C85',
    textAlign: 'center',
    lineHeight: 19.6,
  },
  successFooter: {
    paddingVertical: 24,
    paddingBottom: 32,
  },
});
